import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import SingleSubjectCardHeader from "./SingleSubjectCardHeader";
import SubjectSidebar from "./SubjectSidebar";

import FetchWrapper from "../../../components/UI/FetchWraper";
import { useFetch } from "../../../hooks/FetchHook";
import { createSidebarMenuItems, createContentMap } from "./utils/SSJTabItems";
import useIsFeatureFlagEnabled from "@/hooks/useIsFeatureFlagEnabled";
import {
  getAvailableMenuKeys,
  getFirstAvailableMenuPath,
} from "@/utils/landingPageUtils";
import { useRoleFilter } from "@/hooks/useRoleFilter";
import Error from "@/components/UI/Error";
import ProfileReviewControl from "@/components/ProfileReview/ProfileReviewControl";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { useScopeParams } from "../../../hooks/useScopeParams";

export default function SingleSubjectOverview() {
  const { subjectId } = useScopeParams();
  return (
    <FetchWrapper endpointSuffix={`activities/overview/${subjectId}/`}>
      {(data: unknown) => <SingleSubjectOverviewContent data={data} />}
    </FetchWrapper>
  );
}

interface SingleSubjectOverviewContentProps {
  data: any;
}

function SingleSubjectOverviewContent({
  data,
}: SingleSubjectOverviewContentProps) {
  const { data: subjects } = useFetch({
    endpointSuffix: `subjects/`,
  });

  const { subjectId, id, buildUrl } = useScopeParams();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const isFeatureFlagEnabled = useIsFeatureFlagEnabled();
  const { filterTabGroups } = useRoleFilter();

  const sidebarMenuItems = filterTabGroups(
    createSidebarMenuItems(id, isFeatureFlagEnabled),
  );
  const availableTabKeys = useMemo(
    () => getAvailableMenuKeys(sidebarMenuItems),
    [sidebarMenuItems],
  );
  const firstAvailableTab = getFirstAvailableMenuPath(sidebarMenuItems);
  const hasAvailableTabs = availableTabKeys.length > 0;

  const activeTab = useMemo(() => {
    if (!hasAvailableTabs) return "";

    const segments = path.split("/");
    const subjectIdIndex = segments.indexOf(subjectId);
    const requestedTab = segments[subjectIdIndex + 1];

    if (requestedTab && availableTabKeys.includes(requestedTab)) {
      return requestedTab;
    }

    return firstAvailableTab;
  }, [availableTabKeys, firstAvailableTab, hasAvailableTabs, path, subjectId]);

  useEffect(() => {
    if (!hasAvailableTabs) {
      return;
    }

    const segments = path.split("/").map(decodeURIComponent);
    const subjectIdIndex = segments.indexOf(subjectId);
    const requestedTab = segments[subjectIdIndex + 1];
    const nextTab = activeTab;

    if ((!requestedTab || requestedTab !== nextTab) && nextTab) {
      navigate(buildUrl(`subjects/overview/${subjectId}/${nextTab}`), {
        replace: true,
      });
    }
  }, [activeTab, buildUrl, hasAvailableTabs, navigate, path, subjectId]);

  const blankSubjectInfo = {
    subject: "",
    site: "",
    country: "",
    indication: "",
    sex: "",
    age: "",
    race: "",
    study_status: "",
  };

  const subjectsList = useMemo(
    () =>
      (subjects as any[])?.map((subject: any) => ({
        value: subject.subject,
        label: subject.subject,
      })) || [],
    [subjects],
  );

  const contentMap = createContentMap(subjectId);

  const handleSubjectChange = (newSubjectId: string) => {
    if (!hasAvailableTabs) {
      navigate(buildUrl(`subjects/overview/${newSubjectId}`));
      return;
    }

    const nextTab = availableTabKeys.includes(activeTab)
      ? activeTab
      : firstAvailableTab;
    navigate(buildUrl(`subjects/overview/${newSubjectId}/${nextTab}`), {
      state: location.state,
    });
  };

  const handleMenuSelect = (key: string) => {
    navigate(buildUrl(`subjects/overview/${subjectId}/${key}`), {
      state: location.state,
    });
  };

  const cameFromMedicalReview =
    (location.state as { from?: string } | null)?.from === "medical-review";

  return (
    <div className="flex h-full">
      {hasAvailableTabs ? (
        <SubjectSidebar
          subjectsList={subjectsList}
          menuItems={sidebarMenuItems}
          activeKey={activeTab}
          currentSubjectId={subjectId}
          onSubjectChange={handleSubjectChange}
          onMenuSelect={handleMenuSelect}
          extra={
            isFeatureFlagEnabled(FEATURE_FLAGS.PROFILE_REVIEW) ? (
              <ProfileReviewControl subjectId={subjectId} />
            ) : undefined
          }
        />
      ) : null}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        {cameFromMedicalReview && (
          <div className="px-4 pt-2">
            <Button
              type="link"
              size="small"
              icon={<ArrowLeftOutlined />}
              className="px-0"
              onClick={() => navigate(buildUrl("safety/medical-review"))}
            >
              Back to Medical Review
            </Button>
          </div>
        )}
        <div className="px-4 pt-3 pb-2">
          <SingleSubjectCardHeader
            subjectInfo={
              data.results.length > 0 ? data.results[0] : blankSubjectInfo
            }
          />
        </div>
        <div className="flex-1 min-h-0 p-4 overflow-y-auto">
          {hasAvailableTabs ? (
            contentMap[activeTab] || contentMap[firstAvailableTab] || null
          ) : (
            <Error />
          )}
        </div>
      </div>
    </div>
  );
}
